import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
	Bot,
	User as UserIcon,
	Send,
	Sparkles,
	CircleDashed,
	Loader2,
	CheckCircle2,
	Trash2,
	Plus,
	TrendingUp,
	Calendar as CalendarIcon,
	Flag,
	Clock,
	Target,
	ChevronUp,
	ChevronDown,
} from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import Layout from '@/components/Layout';
import LevelBadge from '@/components/LevelBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { useAuth } from '@/contexts/AuthContext';
import { pillarTitle, PILLARS } from '@/constants/pillars';
import { COACH } from '@/constants/testIds';

const FREQUENCIES = [
	{ value: 'daily', label: 'Daily' },
	{ value: 'weekly', label: 'Weekly' },
	{ value: 'fortnightly', label: 'Fortnightly' },
	{ value: 'monthly', label: 'Monthly' },
	{ value: 'quarterly', label: 'Quarterly' },
];

function difficultyColor(d) {
	const x = (d || '').toLowerCase();
	if (x === 'easy') return 'text-emerald-700 bg-emerald-50';
	if (x === 'medium') return 'text-amber-700 bg-amber-50';
	if (x === 'hard') return 'text-rose-700 bg-rose-50';
	return 'text-slate-600 bg-slate-100';
}

function statusBadge(s) {
	if (s === 'completed') return { label: 'Completed', cls: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 };
	if (s === 'in_progress') return { label: 'In progress', cls: 'bg-blue-100 text-blue-800', icon: Loader2 };
	if (s === 'verification_required') return { label: 'Verify', cls: 'bg-amber-100 text-amber-800', icon: Flag };
	return { label: 'Not started', cls: 'bg-slate-100 text-slate-600', icon: CircleDashed };
}

export default function Coach() {
	const { user } = useAuth();
	const isPremium = user?.plan === 'PREMIUM' || user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN';

	const [tab, setTab] = useState('plan');
	const [projection, setProjection] = useState(null);
	const [tasks, setTasks] = useState([]);
	const [reminders, setReminders] = useState([]);
	const [review, setReview] = useState(null);
	const [prefs, setPrefs] = useState({ frequency: 'weekly' });
	const [genBusy, setGenBusy] = useState(false);

	const [messages, setMessages] = useState([]);
	const [input, setInput] = useState('');
	const [sending, setSending] = useState(false);
	const scrollRef = useRef(null);

	const [newTaskOpen, setNewTaskOpen] = useState(false);
	const [newTask, setNewTask] = useState({ title: '', description: '', category: 'Preparedness', pillar: 'emergency_preparedness', difficulty: 'Easy', time_estimate: '30 minutes', score_gain: 3 });

	const loadAll = async () => {
		try {
			const [proj, t, rem, p] = await Promise.all([
				api.get('/coach/projection'),
				api.get('/tasks'),
				api.get('/coach/reminders'),
				api.get('/coach/preferences'),
			]);
			setProjection(proj.data);
			setTasks(t.data?.tasks || []);
			setReminders(rem.data?.reminders || []);
			setPrefs(p.data || { frequency: 'weekly' });
		} catch (err) {
			toast.error(formatApiError(err));
		}
	};

	const loadHistory = async () => {
		try {
			const { data } = await api.get('/coach/history');
			const msgs = (data?.messages || []).map((m) => ({ role: m.role, text: m.text }));
			setMessages(msgs.length ? msgs : [{ role: 'coach', text: "Hi there. I'm your Family Preparedness Coach. What's on your family's mind?" }]);
		} catch (err) {
			toast.error(formatApiError(err));
		}
	};

	useEffect(() => {
		loadAll();
		loadHistory();
	}, []); // eslint-disable-line

	useEffect(() => {
		scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
	}, [messages]);

	const generateTasks = async () => {
		if (!projection?.has_assessment) {
			toast.error('Take your first assessment to unlock personalized tasks.');
			return;
		}
		setGenBusy(true);
		try {
			const { data } = await api.post('/coach/generate-tasks');
			setTasks(data.tasks || []);
			toast.success(`${data.count || 0} personalized tasks created`);
			await loadAll();
		} catch (err) {
			toast.error(formatApiError(err));
		} finally {
			setGenBusy(false);
		}
	};

	const updateTask = async (t, patch) => {
		try {
			const { data } = await api.put(`/tasks/${t.task_id}`, patch);
			setTasks((arr) => arr.map((x) => (x.task_id === t.task_id ? data.task : x)));
			await loadAll();
		} catch (err) {
			toast.error(formatApiError(err));
		}
	};

	const deleteTask = async (t) => {
		if (!window.confirm(`Remove task "${t.title}"?`)) return;
		try {
			await api.delete(`/tasks/${t.task_id}`);
			setTasks((arr) => arr.filter((x) => x.task_id !== t.task_id));
			await loadAll();
		} catch (err) {
			toast.error(formatApiError(err));
		}
	};

	const createTask = async () => {
		if (!newTask.title.trim()) {
			toast.error('Title is required');
			return;
		}
		try {
			const { data } = await api.post('/tasks', newTask);
			setTasks((arr) => [data.task, ...arr]);
			setNewTaskOpen(false);
			setNewTask({ title: '', description: '', category: 'Preparedness', pillar: 'emergency_preparedness', difficulty: 'Easy', time_estimate: '30 minutes', score_gain: 3 });
			toast.success('Task added');
			await loadAll();
		} catch (err) {
			toast.error(formatApiError(err));
		}
	};

	const setFrequency = async (freq) => {
		try {
			await api.put('/coach/preferences', { frequency: freq });
			setPrefs((p) => ({ ...p, frequency: freq }));
			toast.success(`Coaching cadence set to ${freq}`);
		} catch (err) {
			toast.error(formatApiError(err));
		}
	};

	const loadReview = async () => {
		try {
			const { data } = await api.get('/coach/monthly-review');
			setReview(data);
		} catch (err) {
			toast.error(formatApiError(err));
		}
	};

	const send = async (txt) => {
		const message = (txt ?? input).trim();
		if (!message) return;
		setInput('');
		setMessages((m) => [...m, { role: 'user', text: message }]);
		setSending(true);
		try {
			const { data } = await api.post('/coach/chat', { message });
			setMessages((m) => [...m, { role: 'coach', text: data.reply }]);
		} catch (err) {
			toast.error(formatApiError(err));
		} finally {
			setSending(false);
		}
	};

	const completedCount = useMemo(() => tasks.filter((t) => t.status === 'completed').length, [tasks]);
	const groupedTasks = useMemo(() => {
		const order = ['not_started', 'in_progress', 'verification_required', 'completed'];
		const g = { not_started: [], in_progress: [], verification_required: [], completed: [] };
		for (const t of tasks) (g[t.status] || g.not_started).push(t);
		return order.map((k) => ({ key: k, items: g[k] }));
	}, [tasks]);

	if (!isPremium) {
		return (
			<Layout>
				<div className="max-w-2xl mx-auto card-soft p-10 text-center">
					<div className="mx-auto w-16 h-16 rounded-3xl bg-emerald-100 text-emerald-700 grid place-items-center">
						<Sparkles className="w-7 h-7" />
					</div>
					<h2 className="mt-4 font-heading text-2xl font-semibold text-slate-900">Family Resilience Coach™</h2>
					<p className="mt-2 text-slate-600">Premium members receive personalized coaching to continuously improve their Family Resilience Score — tasks, levels, monthly reports and gentle reminders.</p>
					<Link to="/subscription"><Button className="mt-6 rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF]">Upgrade to Premium</Button></Link>
					<p className="mt-4 text-xs text-slate-500">Free members continue to enjoy assessments, score and the educational library.</p>
				</div>
			</Layout>
		);
	}

	return (
		<Layout>
			<div data-testid={COACH.root} className="enter-stagger space-y-6">
				<div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
					<div>
						<div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">Premium</div>
						<h1 className="mt-2 text-3xl sm:text-4xl font-heading font-bold tracking-tight text-slate-900">Family Resilience Coach™</h1>
						<p className="mt-2 text-slate-600">Tiny, kind, doable steps that compound into a more resilient family.</p>
					</div>
					{projection?.current_level && <LevelBadge level={projection.current_level} size="lg" />}
				</div>

				{/* Projection header */}
				{projection && (
					<div className="card-soft p-6 sm:p-8">
						<div className="grid grid-cols-1 lg:grid-cols-[1.1fr,1fr] gap-8 items-center">
							<div>
								<div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Your score journey</div>
								<div className="mt-3 flex items-baseline gap-4 flex-wrap">
									<div>
										<div className="text-5xl font-heading font-bold text-slate-900 tabular-nums">{projection.current_score}</div>
										<div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mt-1">Current</div>
									</div>
									<div className="flex items-center text-slate-300">→</div>
									<div>
										<div className="text-5xl font-heading font-bold text-emerald-600 tabular-nums">{projection.potential_score}</div>
										<div className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-700 mt-1">Potential</div>
									</div>
									{projection.boost > 0 && (
										<div className="ml-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold text-sm">
											<TrendingUp className="w-3.5 h-3.5" /> +{projection.boost} earned
										</div>
									)}
								</div>
								{projection.next_level && (
									<div className="mt-4 text-slate-700">
										You are <span className="font-bold text-blue-800">{projection.points_to_next_level}</span> points away from becoming a <span className="font-semibold text-slate-900">{projection.next_level.title}</span>.
									</div>
								)}
								<div className="mt-5 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
									<div className="h-full rounded-full bg-gradient-to-r from-blue-700 to-emerald-500 transition-[width] duration-700" style={{ width: `${projection.current_score}%` }} />
								</div>
							</div>

							<div>
								<div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-3">Top opportunities</div>
								{(projection.top_opportunities || []).length === 0 ? (
									<div className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-600">
										No active tasks yet. Generate your personalized plan to start.
									</div>
								) : (
									<ul className="space-y-2">
										{projection.top_opportunities.slice(0, 6).map((o) => (
											<li key={o.task_id} className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50 hover:bg-slate-100 transition-colors">
												<span className="inline-flex items-center justify-center w-10 h-7 rounded-full bg-emerald-100 text-emerald-700 font-bold text-sm tabular-nums">+{o.score_gain}</span>
												<span className="text-sm text-slate-800 flex-1 truncate">{o.title}</span>
												<span className="text-xs text-slate-500 hidden sm:inline">{o.category}</span>
											</li>
										))}
									</ul>
								)}
							</div>
						</div>
					</div>
				)}

				{/* Reminders */}
				{reminders?.length > 0 && (
					<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
						{reminders.slice(0, 3).map((r) => (
							<div key={r.key} className="card-soft p-5">
								<div className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-700">{r.title}</div>
								<div className="mt-2 text-slate-700 text-sm leading-relaxed">{r.body}</div>
							</div>
						))}
					</div>
				)}

				<Tabs value={tab} onValueChange={setTab} className="w-full">
					<TabsList className="bg-slate-100 rounded-full p-1 h-11">
						<TabsTrigger value="plan" className="rounded-full px-4" data-testid="coach-tab-plan"><Target className="w-4 h-4 mr-2" /> Improvement Plan</TabsTrigger>
						<TabsTrigger value="chat" className="rounded-full px-4" data-testid="coach-tab-chat"><Bot className="w-4 h-4 mr-2" /> Chat</TabsTrigger>
						<TabsTrigger value="review" className="rounded-full px-4" data-testid="coach-tab-review"><CalendarIcon className="w-4 h-4 mr-2" /> Monthly Review</TabsTrigger>
						<TabsTrigger value="settings" className="rounded-full px-4" data-testid="coach-tab-settings">Settings</TabsTrigger>
					</TabsList>

					{/* PLAN */}
					<TabsContent value="plan" className="mt-6 space-y-4">
						<div className="flex items-center justify-between gap-3 flex-wrap">
							<div className="text-sm text-slate-600">{completedCount} of {tasks.length} tasks completed</div>
							<div className="flex gap-2">
								<Button variant="outline" className="rounded-full" onClick={() => setNewTaskOpen(true)} data-testid="coach-add-task-button">
									<Plus className="w-4 h-4 mr-2" /> Add task
								</Button>
								<Button onClick={generateTasks} disabled={genBusy} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="coach-generate-tasks-button">
									{genBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4 mr-2" /> {tasks.length ? 'Regenerate plan' : 'Generate personalized plan'}</>}
								</Button>
							</div>
						</div>

						{tasks.length === 0 ? (
							<div className="card-soft p-10 text-center">
								<Sparkles className="w-7 h-7 mx-auto text-emerald-500" />
								<h3 className="mt-3 font-heading text-xl font-semibold">Your personalized roadmap is one click away</h3>
								<p className="text-slate-600 mt-2 max-w-md mx-auto text-sm">After your assessment, the coach will create concrete, kind tasks for the pillars that need the most love — each with an estimated score gain.</p>
							</div>
						) : (
							<div className="space-y-4">
								{groupedTasks.map((grp) => grp.items.length > 0 && (
									<section key={grp.key}>
										<div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-3">{statusBadge(grp.key).label}</div>
										<div className="space-y-3">
											{grp.items.map((t) => {
												const sb = statusBadge(t.status);
												const SIcon = sb.icon;
												return (
													<div key={t.task_id} className="card-soft p-5 group" data-testid="coach-task-row">
														<div className="flex items-start gap-3">
															<button
																type="button"
																onClick={() => updateTask(t, { status: t.status === 'completed' ? 'not_started' : 'completed' })}
																className={`mt-0.5 w-6 h-6 rounded-full grid place-items-center shrink-0 transition-all ${t.status === 'completed' ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-400 hover:bg-emerald-100 hover:text-emerald-600'}`}
																aria-label="Toggle complete"
																data-testid="coach-task-toggle"
															>
																{t.status === 'completed' ? <CheckCircle2 className="w-4 h-4" /> : <CircleDashed className="w-4 h-4" />}
															</button>
															<div className="flex-1 min-w-0">
																<div className="flex items-start justify-between gap-3 flex-wrap">
																	<div className="min-w-0">
																		<div className="flex items-center gap-2 flex-wrap">
																			<span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">{pillarTitle(t.pillar)} · {t.category}</span>
																			<span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${difficultyColor(t.difficulty)}`}>{t.difficulty}</span>
																			<span className="text-[10px] text-slate-500 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {t.time_estimate}</span>
																		</div>
																		<div className={`mt-1 font-heading text-lg font-semibold leading-snug ${t.status === 'completed' ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
																			{t.title}
																		</div>
																		<div className="mt-1 text-sm text-slate-600 leading-relaxed">{t.description}</div>
																	</div>
																	<div className="flex items-center gap-2 shrink-0">
																		<span className="inline-flex items-center justify-center min-w-[2.25rem] h-8 px-2 rounded-full bg-emerald-50 text-emerald-700 font-bold text-sm tabular-nums">+{t.score_gain}</span>
																		<span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${sb.cls}`}>
																			<SIcon className="w-3 h-3" /> {sb.label}
																		</span>
																	</div>
																</div>

																{/* Progress + status switcher */}
																<div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr,auto,auto] gap-3 items-center">
																	<div className="flex items-center gap-3">
																		<div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
																			<div className="h-full rounded-full bg-gradient-to-r from-blue-700 to-emerald-500 transition-[width] duration-500" style={{ width: `${t.progress_pct || 0}%` }} />
																		</div>
																		<div className="text-xs font-semibold text-slate-600 tabular-nums w-10 text-right">{t.progress_pct || 0}%</div>
																	</div>
																	<Slider
																		value={[t.progress_pct || 0]}
																		onValueCommit={(v) => updateTask(t, { progress_pct: v[0] })}
																		onValueChange={(v) => setTasks((arr) => arr.map((x) => (x.task_id === t.task_id ? { ...x, progress_pct: v[0] } : x)))}
																		min={0}
																		max={100}
																		step={5}
																		className="w-40"
																		data-testid="coach-task-slider"
																	/>
																	<div className="flex gap-1.5">
																		<Select value={t.status} onValueChange={(v) => updateTask(t, { status: v })}>
																			<SelectTrigger className="h-9 w-36 rounded-full" data-testid="coach-task-status-select"><SelectValue /></SelectTrigger>
																			<SelectContent>
																				<SelectItem value="not_started">Not started</SelectItem>
																				<SelectItem value="in_progress">In progress</SelectItem>
																				<SelectItem value="verification_required">Verify</SelectItem>
																				<SelectItem value="completed">Completed</SelectItem>
																			</SelectContent>
																		</Select>
																		<Button size="icon" variant="ghost" className="rounded-full text-rose-600 hover:bg-rose-50" onClick={() => deleteTask(t)} aria-label="Delete task">
																			<Trash2 className="w-4 h-4" />
																		</Button>
																	</div>
																</div>
															</div>
														</div>
													</div>
												);
											})}
										</div>
									</section>
								))}
							</div>
						)}
					</TabsContent>

					{/* CHAT */}
					<TabsContent value="chat" className="mt-6">
						<div className="card-soft flex flex-col overflow-hidden h-[60vh] min-h-[480px]">
							<div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4 bg-blue-50/30">
								{messages.map((m, i) => (
									<div key={i} className={`flex gap-3 ${m.role === 'user' ? 'flex-row-reverse' : ''}`} data-testid={COACH.message}>
										<div className={`w-8 h-8 rounded-full grid place-items-center shrink-0 ${m.role === 'user' ? 'bg-slate-200 text-slate-700' : 'bg-gradient-to-br from-blue-700 to-emerald-500 text-white'}`}>
											{m.role === 'user' ? <UserIcon className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
										</div>
										<div className={`max-w-[80%] px-4 py-3 rounded-2xl shadow-sm text-sm leading-relaxed ${m.role === 'user' ? 'bg-blue-900 text-white rounded-tr-md' : 'bg-white text-slate-700 rounded-tl-md border border-slate-100'}`}>
											{m.text}
										</div>
									</div>
								))}
								{sending && (
									<div className="flex gap-3 items-center text-slate-500 text-sm">
										<div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-700 to-emerald-500 grid place-items-center text-white">
											<Bot className="w-4 h-4" />
										</div>
										<span className="animate-pulse-soft">Thinking…</span>
									</div>
								)}
							</div>
							<div className="border-t border-slate-100 p-4">
								<div className="flex gap-2">
									<Textarea
										value={input}
										onChange={(e) => setInput(e.target.value)}
										onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
										placeholder="Tell your coach what's on your family's mind…"
										className="resize-none rounded-2xl min-h-[52px] max-h-32"
										data-testid={COACH.input}
									/>
									<Button type="button" onClick={() => send()} disabled={sending || !input.trim()} className="rounded-full bg-emerald-600 hover:bg-emerald-700 h-12 px-5 gap-2 shrink-0" data-testid={COACH.sendButton}>
										<Send className="w-4 h-4" /> Send
									</Button>
								</div>
							</div>
						</div>
					</TabsContent>

					{/* MONTHLY REVIEW */}
					<TabsContent value="review" className="mt-6">
						<div className="card-soft p-6">
							<div className="flex items-center justify-between gap-2 flex-wrap">
								<h3 className="font-heading text-xl font-semibold text-slate-900">Monthly Family Preparedness Report</h3>
								<Button onClick={loadReview} variant="outline" className="rounded-full" data-testid="coach-load-review-button">
									<CalendarIcon className="w-4 h-4 mr-2" /> Generate report
								</Button>
							</div>
							{!review ? (
								<p className="mt-3 text-slate-600 text-sm">Click &quot;Generate report&quot; to see your month at a glance — score change, completed tasks, achievement and next month&apos;s mission.</p>
							) : (
								<div className="mt-4 space-y-5">
									<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
										<div className="rounded-2xl bg-slate-50 p-4">
											<div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Current Score</div>
											<div className="mt-1 text-3xl font-heading font-bold text-slate-900">{review.current_score}</div>
										</div>
										<div className="rounded-2xl bg-slate-50 p-4">
											<div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Previous</div>
											<div className="mt-1 text-3xl font-heading font-bold text-slate-900">{review.previous_score}</div>
										</div>
										<div className={`rounded-2xl p-4 ${review.improvement >= 0 ? 'bg-emerald-50' : 'bg-rose-50'}`}>
											<div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 inline-flex items-center gap-1">
												Change {review.improvement >= 0 ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
											</div>
											<div className={`mt-1 text-3xl font-heading font-bold ${review.improvement >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{review.improvement >= 0 ? '+' : ''}{review.improvement}</div>
										</div>
										<div className="rounded-2xl bg-slate-50 p-4">
											<div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Tasks done</div>
											<div className="mt-1 text-3xl font-heading font-bold text-slate-900">{review.completed_count}<span className="text-slate-400 text-base">/{review.completed_count + review.pending_count}</span></div>
										</div>
									</div>
									<div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-5 text-emerald-900">
										<div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">Monthly achievement</div>
										<div className="mt-1 font-heading text-lg font-semibold">{review.monthly_achievement}</div>
									</div>
									<div>
										<div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">Summary</div>
										<p className="text-slate-700 leading-relaxed">{review.summary}</p>
									</div>
									<div className="rounded-2xl bg-blue-50 border border-blue-100 p-5 text-blue-900">
										<div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">Next month mission</div>
										<div className="mt-1 font-heading text-lg font-semibold">{review.next_month_mission}</div>
									</div>
								</div>
							)}
						</div>
					</TabsContent>

					{/* SETTINGS */}
					<TabsContent value="settings" className="mt-6">
						<div className="card-soft p-6 max-w-xl">
							<div className="font-heading text-xl font-semibold text-slate-900">Coaching cadence</div>
							<p className="text-slate-600 mt-2 text-sm">How often would you like a fresh mission and gentle nudge?</p>
							<div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-2">
								{FREQUENCIES.map((f) => (
									<button
										key={f.value}
										type="button"
										onClick={() => setFrequency(f.value)}
										data-testid={`coach-frequency-${f.value}`}
										className={`px-3 py-2.5 rounded-full text-sm font-semibold border-2 transition-all ${prefs.frequency === f.value ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-700 hover:border-slate-300'}`}
									>
										{f.label}
									</button>
								))}
							</div>
						</div>
					</TabsContent>
				</Tabs>

				<Dialog open={newTaskOpen} onOpenChange={setNewTaskOpen}>
					<DialogContent className="rounded-3xl">
						<DialogHeader>
							<DialogTitle>Add a custom task</DialogTitle>
						</DialogHeader>
						<div className="space-y-3 mt-2">
							<Input placeholder="Title" value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} className="h-11 rounded-xl" data-testid="coach-new-task-title" />
							<Textarea placeholder="Description (optional)" value={newTask.description} onChange={(e) => setNewTask({ ...newTask, description: e.target.value })} className="rounded-xl min-h-[80px]" />
							<div className="grid grid-cols-2 gap-3">
								<Select value={newTask.pillar} onValueChange={(v) => setNewTask({ ...newTask, pillar: v })}>
									<SelectTrigger className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
									<SelectContent>
										{PILLARS.map((p) => <SelectItem key={p.key} value={p.key}>{p.title}</SelectItem>)}
									</SelectContent>
								</Select>
								<Select value={newTask.difficulty} onValueChange={(v) => setNewTask({ ...newTask, difficulty: v })}>
									<SelectTrigger className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
									<SelectContent>
										<SelectItem value="Easy">Easy</SelectItem>
										<SelectItem value="Medium">Medium</SelectItem>
										<SelectItem value="Hard">Hard</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<div className="grid grid-cols-2 gap-3">
								<Input placeholder="Category (e.g. Emergency Fund)" value={newTask.category} onChange={(e) => setNewTask({ ...newTask, category: e.target.value })} className="h-11 rounded-xl" />
								<Input placeholder="Time estimate" value={newTask.time_estimate} onChange={(e) => setNewTask({ ...newTask, time_estimate: e.target.value })} className="h-11 rounded-xl" />
							</div>
							<div>
								<div className="text-sm text-slate-700 mb-1">Score gain: <span className="font-bold">+{newTask.score_gain}</span></div>
								<Slider value={[newTask.score_gain]} onValueChange={(v) => setNewTask({ ...newTask, score_gain: v[0] })} min={1} max={10} step={1} />
							</div>
						</div>
						<DialogFooter>
							<Button variant="outline" className="rounded-full" onClick={() => setNewTaskOpen(false)}>Cancel</Button>
							<Button className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={createTask} data-testid="coach-new-task-save">Add task</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>
		</Layout>
	);
}
